import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { LayoutDashboard, Inbox, MessageSquareText, Plug, Building2, LogOut, Settings, Wrench, ClipboardList, Workflow, ShieldCheck, ChevronDown, ChevronLeft, ChevronRight, ShoppingCart, ScrollText, Mail, X, Coins, LifeBuoy, Database, Banknote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MAIN_TOP = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
];
const COMM_CHILDREN = [
  { to: "/inbox", label: "Posta", icon: Inbox, testid: "nav-inbox" },
  { to: "/pratiche", label: "Pratiche AI", icon: ClipboardList, testid: "nav-pratiche" },
  { to: "/connetti", label: "Gestione account", icon: Plug, testid: "nav-connetti" },
];
const COMM_PATHS = COMM_CHILDREN.map((c) => c.to);
const CONDO_CHILDREN = [
  { to: "/condomini", label: "Gestione condomìni", icon: Building2, testid: "nav-condomini" },
  { to: "/portali-condomini", label: "Portali Condòmini", icon: ShieldCheck, testid: "nav-portali" },
  { to: "/fornitori", label: "Fornitori", icon: Wrench, testid: "nav-fornitori" },
  { to: "/workflow", label: "Workflow", icon: Workflow, testid: "nav-workflow" },
];
const CONDO_PATHS = CONDO_CHILDREN.map((c) => c.to);
const MAIN_BOTTOM = [
  { to: "/chat", label: "Chat AI", icon: MessageSquareText, testid: "nav-chat" },
];

// Pallino rosso di notifica sopra l'icona (usato quando la barra è compressa).
const Dot = ({ testid, urgent = false }) => (
  <span data-testid={testid} className={`absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-[#0A0A0A] ${urgent ? "badge-urgent-pulse" : ""}`} />
);

// className per una voce di navigazione: centrata e senza etichetta quando la barra è compressa.
const navClass = (collapsed, extra = "") => ({ isActive }) =>
  `relative flex items-center rounded-md text-sm font-medium transition-colors duration-200 ${extra} ${collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3.5 py-2.5"} ${isActive ? "bg-[#007BFF] text-white" : "text-white/60 hover:text-white hover:bg-white/[0.06]"}`;

export const Sidebar = ({ open = false, onClose = () => {}, collapsed = false, onToggleCollapse = () => {}, hoverExpand = false, onHoverExpandChange = () => {} }) => {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [condoOpen, setCondoOpen] = useState(CONDO_PATHS.some((p) => loc.pathname.startsWith(p)));
  const [commOpen, setCommOpen] = useState(COMM_PATHS.some((p) => loc.pathname.startsWith(p)));
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(false);
  const lastId = useRef(null);
  const firstLoad = useRef(true);
  const [praticheNew, setPraticheNew] = useState(0);
  const [praticheUrgent, setPraticheUrgent] = useState(false);
  const [pratichePulse, setPratichePulse] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const lastPraticaId = useRef(null);
  const firstPraticaLoad = useRef(true);
  const lastSupportReplyId = useRef(null);
  const firstSupportLoad = useRef(true);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await api.get("/notifications/summary", { params: { account_id: "all" } });
        if (!alive) return;
        setUnread(r.data.inbox_unread || 0);
        const lid = r.data.latest_id;
        if (firstLoad.current) {
          lastId.current = lid;
          firstLoad.current = false;
        } else if (lid && lid !== lastId.current) {
          lastId.current = lid;
          setPulse(true);
          setTimeout(() => setPulse(false), 1300);
          const sender = r.data.latest_sender || "Nuovo mittente";
          const subject = r.data.latest_subject || "(nessun oggetto)";
          const preview = r.data.latest_preview || "";
          toast.custom((tid) => (
            <div data-testid="new-mail-preview"
              onClick={() => { toast.dismiss(tid); navigate(lid ? `/inbox?mail=${encodeURIComponent(lid)}` : "/inbox"); }}
              className="w-[360px] max-w-[86vw] cursor-pointer bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in slide-in-from-right-4">
              <div className="h-1 bg-[#007BFF]" />
              <div className="flex items-start gap-3 p-3.5">
                <div className="h-9 w-9 rounded-full bg-[#007BFF]/10 flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-[#007BFF]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#007BFF]">Nuova email</span>
                    <button onClick={(e) => { e.stopPropagation(); toast.dismiss(tid); }} className="text-gray-300 hover:text-gray-500"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="text-sm font-bold text-gray-900 truncate">{sender}</div>
                  <div className="text-xs font-medium text-gray-700 truncate">{subject}</div>
                  {preview && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{preview}</div>}
                </div>
              </div>
            </div>
          ), { duration: 8000 });
        }
      } catch (e) { console.error("Errore polling notifiche:", e); }
      try {
        const rp = await api.get("/pratiche/unseen-count");
        if (!alive) return;
        setPraticheNew(rp.data.count || 0);
        setPraticheUrgent(!!rp.data.has_urgent);
        const pid = rp.data.latest_id;
        if (firstPraticaLoad.current) {
          lastPraticaId.current = pid;
          firstPraticaLoad.current = false;
        } else if (pid && pid !== lastPraticaId.current) {
          lastPraticaId.current = pid;
          setPratichePulse(true);
          setTimeout(() => setPratichePulse(false), 1300);
          toast.success("Nuova segnalazione ricevuta", { description: rp.data.latest_subject || "Vai in Pratiche AI", icon: <ClipboardList className="h-4 w-4" /> });
        }
      } catch (e) { console.debug("polling pratiche:", e); }
      try {
        const rs = await api.get("/support/unread-count");
        if (!alive) return;
        setSupportUnread(rs.data.count || 0);
        const srid = rs.data.latest_reply_id;
        if (firstSupportLoad.current) {
          lastSupportReplyId.current = srid;
          firstSupportLoad.current = false;
        } else if (srid && srid !== lastSupportReplyId.current) {
          lastSupportReplyId.current = srid;
          const msg = rs.data.is_super ? "L'amministratore ha risposto" : "L'Assistenza ha risposto — tocca a te";
          toast.success(msg, { description: rs.data.latest_reply_subject || "Vai in Assistenza", icon: <LifeBuoy className="h-4 w-4" /> });
        }
      } catch (e) { console.debug("polling assistenza:", e); }
    };
    poll();
    const t = setInterval(poll, 15000);
    window.addEventListener("pratiche-updated", poll);
    return () => { alive = false; clearInterval(t); window.removeEventListener("pratiche-updated", poll); };
  }, [navigate]);

  // Sulla vista mobile (menu a comparsa) mostra sempre la barra estesa; comprimi solo su desktop.
  // Nelle sezioni ad auto-riduzione (hoverExpand) la barra si riapre al passaggio del mouse.
  // Comunichiamo lo stato al layout così il contenuto si adatta (nessuna sovrapposizione).
  const [hovered, setHovered] = useState(false);
  const hoverOpen = hoverExpand && collapsed && hovered && !open;
  const isCollapsed = collapsed && !open && !hoverOpen;
  useEffect(() => { onHoverExpandChange(hoverOpen); }, [hoverOpen, onHoverExpandChange]);

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={onClose} data-testid="sidebar-backdrop" />}
      <aside data-testid="app-sidebar" data-collapsed={isCollapsed}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        className={`${isCollapsed ? "w-[76px]" : "w-64"} shrink-0 h-screen bg-[#0A0A0A] text-white flex flex-col fixed left-0 top-0 z-40 border-r border-white/10 transition-all duration-300 md:translate-x-0 ${hoverOpen ? "shadow-2xl shadow-black/50" : ""} ${open ? "translate-x-0" : "-translate-x-full"}`}>
      <button data-testid="sidebar-collapse-toggle" onClick={onToggleCollapse}
        title={isCollapsed ? "Espandi barra" : "Comprimi barra"} aria-label={isCollapsed ? "Espandi barra" : "Comprimi barra"}
        className="hidden md:flex absolute -right-3 top-8 h-6 w-6 items-center justify-center rounded-full bg-[#007BFF] text-white shadow-lg border border-white/20 hover:bg-[#0069D9] transition-colors z-50">
        {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
      </button>
      <div className={`border-b border-white/10 ${isCollapsed ? "px-3 py-7" : "px-6 py-7"}`}>
        <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2.5"}`}>
          <img src="/sedio-logo.png" alt="Sedio" className="h-10 w-auto object-contain" data-testid="sidebar-logo" />
          {!isCollapsed && (
            <div>
              <div className="font-head text-lg font-extrabold tracking-tight leading-none">Sedio</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">Condominio AI</div>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto overflow-x-hidden">
        {MAIN_TOP.map(({ to, label, icon: Icon, testid }) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={testid} onClick={onClose} title={isCollapsed ? label : undefined}
            className={navClass(isCollapsed)}>
            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            {!isCollapsed && <span className="flex-1">{label}</span>}
          </NavLink>
        ))}

        {/* Gruppo Comunicazioni (espandibile): Posta, Pratiche AI, Gestione account */}
        <div>
          {isCollapsed ? (
            <button type="button" data-testid="nav-comunicazioni-group" title="Comunicazioni"
              onClick={() => { onToggleCollapse(); setCommOpen(true); }}
              className={`relative w-full flex items-center justify-center px-0 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${COMM_PATHS.some((p) => loc.pathname.startsWith(p)) ? "text-white bg-white/[0.06]" : "text-white/60 hover:text-white hover:bg-white/[0.06]"}`}>
              <Inbox className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
              {(unread > 0 || praticheNew > 0) && <Dot testid="comunicazioni-badge-dot" urgent={praticheUrgent} />}
            </button>
          ) : (
          <button
            type="button"
            data-testid="nav-comunicazioni-group"
            onClick={() => setCommOpen((o) => !o)}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${COMM_PATHS.some((p) => loc.pathname.startsWith(p)) ? "text-white" : "text-white/60 hover:text-white hover:bg-white/[0.06]"}`}>
            <Inbox className="h-[18px] w-[18px]" strokeWidth={2} />
            <span className="flex-1 text-left">Comunicazioni</span>
            {!commOpen && unread > 0 && (
              <span data-testid="inbox-unread-badge-collapsed"
                className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold ${pulse ? "pop-in" : ""}`}>
                {unread > 99 ? "99+" : unread}
              </span>
            )}
            {!commOpen && praticheNew > 0 && (
              <span data-testid="pratiche-new-badge-collapsed"
                className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-white text-[11px] font-bold bg-red-500 ${praticheUrgent ? "badge-urgent-pulse" : ""}`}
                title={praticheUrgent ? "Pratiche urgenti/emergenza" : "Nuove pratiche aperte dall'AI"}>
                {praticheNew > 99 ? "99+" : praticheNew}
              </span>
            )}
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${commOpen ? "rotate-180" : ""}`} />
          </button>
          )}
          {!isCollapsed && commOpen && (
            <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
              {COMM_CHILDREN.map(({ to, label, icon: Icon, testid }) => (
                <NavLink key={to} to={to} data-testid={testid} onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors duration-200 ${isActive ? "bg-[#007BFF] text-white" : "text-white/55 hover:text-white hover:bg-white/[0.06]"}`}>
                  <Icon className="h-4 w-4" strokeWidth={2} />
                  <span className="flex-1">{label}</span>
                  {to === "/inbox" && unread > 0 && (
                    <span data-testid="inbox-unread-badge"
                      className={`ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold ${pulse ? "pop-in" : ""}`}
                      style={pulse ? { animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both, badgePulse 1.3s ease" } : undefined}>
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                  {to === "/pratiche" && praticheNew > 0 && (
                    <span data-testid="pratiche-new-badge"
                      className={`ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-white text-[11px] font-bold bg-red-500 ${praticheUrgent ? "badge-urgent-pulse" : ""} ${pratichePulse ? "pop-in" : ""}`}
                      title={praticheUrgent ? "Pratiche urgenti/emergenza da vedere" : "Nuove pratiche aperte dall'AI"}>
                      {praticheNew > 99 ? "99+" : praticheNew}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Gruppo Condomìni (espandibile) */}
        <div>
          {isCollapsed ? (
            <button type="button" data-testid="nav-condomini-group" title="Condomìni"
              onClick={() => { onToggleCollapse(); setCondoOpen(true); }}
              className={`relative w-full flex items-center justify-center px-0 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${CONDO_PATHS.some((p) => loc.pathname.startsWith(p)) ? "text-white bg-white/[0.06]" : "text-white/60 hover:text-white hover:bg-white/[0.06]"}`}>
              <Building2 className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            </button>
          ) : (
          <button
            type="button"
            data-testid="nav-condomini-group"
            onClick={() => setCondoOpen((o) => !o)}
            className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${CONDO_PATHS.some((p) => loc.pathname.startsWith(p)) ? "text-white" : "text-white/60 hover:text-white hover:bg-white/[0.06]"}`}>
            <Building2 className="h-[18px] w-[18px]" strokeWidth={2} />
            <span className="flex-1 text-left">Condomìni</span>
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${condoOpen ? "rotate-180" : ""}`} />
          </button>
          )}
          {!isCollapsed && condoOpen && (
            <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
              {CONDO_CHILDREN.map(({ to, label, icon: Icon, testid }) => (
                <NavLink key={to} to={to} data-testid={testid} onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors duration-200 ${isActive ? "bg-[#007BFF] text-white" : "text-white/55 hover:text-white hover:bg-white/[0.06]"}`}>
                  <Icon className="h-4 w-4" strokeWidth={2} />
                  <span className="flex-1">{label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {MAIN_BOTTOM.map(({ to, label, icon: Icon, testid }) => (
          <NavLink key={to} to={to} data-testid={testid} onClick={onClose} title={isCollapsed ? label : undefined}
            className={navClass(isCollapsed)}>
            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            {!isCollapsed && <span className="flex-1">{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className={`border-t border-white/10 ${isCollapsed ? "px-2 py-4" : "px-4 py-4"}`}>
        {user?.role === "super_admin" && (
          <NavLink to="/admin/omi" data-testid="nav-admin-omi" onClick={onClose} title={isCollapsed ? "Dataset OMI" : undefined}
            className={navClass(isCollapsed, "mb-2")}>
            <Database className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            {!isCollapsed && <span className="flex-1">Dataset OMI</span>}
          </NavLink>
        )}
        {user?.role === "super_admin" && (
          <NavLink to="/registro-attivita" data-testid="nav-registro-attivita" onClick={onClose} title={isCollapsed ? "Registro attività" : undefined}
            className={navClass(isCollapsed, "mb-2")}>
            <ScrollText className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            {!isCollapsed && <span className="flex-1">Registro attività</span>}
          </NavLink>
        )}
        {user?.role === "super_admin" && (
          <NavLink to="/admin/sepa-dd" data-testid="nav-admin-sepa-dd" onClick={onClose} title={isCollapsed ? "Addebiti SEPA" : undefined}
            className={navClass(isCollapsed, "mb-2")}>
            <Banknote className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            {!isCollapsed && <span className="flex-1">Addebiti SEPA</span>}
          </NavLink>
        )}
        <NavLink to="/sedio-partner" data-testid="nav-sedio-partner" onClick={onClose} title={isCollapsed ? "Sedio Partner" : undefined}
          className={navClass(isCollapsed, "mb-2")}>
          <Coins className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
          {!isCollapsed && <span className="flex-1">Sedio Partner</span>}
        </NavLink>
        <NavLink to="/acquisti" data-testid="nav-acquisti" onClick={onClose} title={isCollapsed ? "Acquisti e collegamento" : undefined}
          className={navClass(isCollapsed, "mb-2")}>
          <ShoppingCart className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
          {!isCollapsed && <span className="flex-1">Acquisti e collegamento</span>}
        </NavLink>
        <NavLink to="/assistenza" data-testid="nav-assistenza" onClick={onClose} title={isCollapsed ? "Assistenza" : undefined}
          className={navClass(isCollapsed, "mb-2")}>
          <LifeBuoy className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
          {!isCollapsed && <span className="flex-1">Assistenza</span>}
          {supportUnread > 0 && (isCollapsed ? (
            <Dot testid="support-unread-badge-dot" />
          ) : (
            <span data-testid="support-unread-badge" className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold">
              {supportUnread > 99 ? "99+" : supportUnread}
            </span>
          ))}
        </NavLink>
        <NavLink to="/impostazioni" data-testid="nav-impostazioni" onClick={onClose} title={isCollapsed ? "Impostazioni" : undefined}
          className={navClass(isCollapsed, "mb-2")}>
          <Settings className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
          {!isCollapsed && <span className="flex-1">Impostazioni</span>}
        </NavLink>
        {user && (
          <NavLink
            to="/profilo"
            data-testid="nav-profilo"
            onClick={onClose}
            title={isCollapsed ? (user.name || "Amministratore") : undefined}
            className={({ isActive }) => `flex items-center rounded-md transition-colors duration-200 ${isCollapsed ? "justify-center px-2 py-1.5" : "gap-2.5 px-2 py-1.5"} mb-2 ${isActive ? "bg-white/[0.08]" : "hover:bg-white/[0.06]"}`}
          >
            {user.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/15 shrink-0" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[#007BFF] flex items-center justify-center text-xs font-semibold shrink-0">
                {(user.name || user.email || "?").charAt(0).toUpperCase()}
              </div>
            )}
            {!isCollapsed && (
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{user.name || "Amministratore"}</div>
                <div className="text-[10px] text-white/40 truncate">{user.email}</div>
              </div>
            )}
          </NavLink>
        )}
        <button
          data-testid="logout-button"
          onClick={logout}
          title={isCollapsed ? "Esci" : undefined}
          className={`w-full flex items-center rounded-md text-sm font-medium text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors duration-200 ${isCollapsed ? "justify-center px-0 py-2.5" : "gap-2.5 px-3.5 py-2.5"}`}
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
          {!isCollapsed && "Esci"}
        </button>
      </div>
    </aside>
    </>
  );
};
